import { CSSProperties, FC, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  size,
} from "@floating-ui/dom";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { Calendar } from "react-date-range";
import "./styles/single-date-picker.css";
import { SingleDatePickerProps } from "./types";

const SingleDatePicker: FC<SingleDatePickerProps> = ({
  containerClassName = "",
  inputClassName = "",
  labelClassName,
  label,
  Icon,
  iconLeft = false,
  value,
  onChange,
  disabled = false,
  disablePastDates = false,
  minDate,
  maxDate,
  iconClassName = "w-5 h-5 text-gray-placeholder dark:text-gray-placeholder",
  placeholder = "dd-mm-yyyy",
  calendarClassName,
  calendarContainerClassName,
  isOpen: externalIsOpen,
  onOpenChange,
  scrollIntoView = false,
  menuPortalTarget,
  calendarPlacement = "bottom-start",
  disableAutoUpdate = false,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = onOpenChange || setInternalIsOpen;
  const inputRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const isDarkMode = useSelector(
    (state: { themeProvider: { isDarkMode: boolean } }) =>
      state.themeProvider.isDarkMode
  );

  const [portalStyle, setPortalStyle] = useState<CSSProperties>({});
  const hasPositionedRef = useRef(false);

  const handleDateChange = (date: Date) => {
    onChange(date);
    setIsOpen(false);
  };

  const handleContainerClick = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (!isOpen) {
      hasPositionedRef.current = false;
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideTrigger =
        inputRef.current && inputRef.current.contains(target);
      const insideCalendar =
        calendarRef.current && calendarRef.current.contains(target);
      if (!insideTrigger && !insideCalendar) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    if (scrollIntoView) {
      inputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, scrollIntoView, setIsOpen]);

  // Lock the page scroll while the calendar is open so users can't drift the
  // trigger out from under the popup. Snapshot the original value so we don't
  // clobber any pre-existing lock (nested modals, etc.).
  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  // Position the calendar via Floating UI for BOTH portaled and inline modes.
  // `strategy: "fixed"` keeps the popup viewport-anchored so it escapes any
  // `overflow:hidden` scroll ancestor; `flip` opens upward when there isn't
  // room below; `offset(4)` guarantees a 4px gap on whichever side it lands.
  useEffect(() => {
    if (!isOpen) return;
    const trigger = inputRef.current;
    const floating = calendarRef.current;
    if (!trigger || !floating) return;

    const updatePosition = () => {
      computePosition(trigger, floating, {
        strategy: "fixed",
        placement: calendarPlacement,
        middleware: [
          offset(4),
          flip({ padding: 8 }),
          shift({ padding: 8 }),
          size({
            apply({ availableHeight, elements }) {
              elements.floating.style.maxHeight = `${Math.min(
                360,
                availableHeight - 8
              )}px`;
            },
          }),
        ],
      }).then(({ x, y }) => {
        if (!hasPositionedRef.current) {
          hasPositionedRef.current = true;
          setPortalStyle({
            position: "fixed",
            top: y,
            left: x,
            visibility: "visible",
          });
        } else {
          floating.style.top = `${y}px`;
          floating.style.left = `${x}px`;
        }
      });
    };

    if (disableAutoUpdate) {
      updatePosition();
      return;
    }

    return autoUpdate(trigger, floating, updatePosition);
  }, [isOpen, calendarPlacement, disableAutoUpdate]);

  const getDateConstraints = () => {
    let minDateValue: Date | undefined;
    let maxDateValue: Date | undefined;

    if (minDate) {
      minDateValue = new Date(minDate);
      minDateValue.setHours(0, 0, 0, 0);
    } else if (disablePastDates) {
      minDateValue = new Date();
      minDateValue.setHours(0, 0, 0, 0);
    }

    if (maxDate) {
      maxDateValue = new Date(maxDate);
      maxDateValue.setHours(23, 59, 59, 999);
    }

    return { minDateValue, maxDateValue };
  };

  const { minDateValue, maxDateValue } = getDateConstraints();

  const calendarMarkup = isOpen ? (
    <div
      ref={calendarRef}
      className={`bg-pure-white dark:bg-pure-white-dk isolate ${calendarContainerClassName ?? ""}`}
      style={{
        ...(hasPositionedRef.current
          ? portalStyle
          : { position: "fixed", visibility: "hidden" }),
        zIndex: 9999999,
      }}
      data-modal-ignore-outside
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Calendar
        date={value ? new Date(value) : new Date()}
        onChange={handleDateChange}
        minDate={minDateValue}
        maxDate={maxDateValue}
        className={`single-date-calendar  ${
          isDarkMode ? "single-date-calendar-dk" : ""
        } rounded-[8px] border border-border-gray dark:border-border-gray-dk relative isolate ${calendarClassName ?? ""}`}
        showDateDisplay={false}
        showMonthAndYearPickers={false}
      />
    </div>
  ) : null;

  return (
    <div className={`flex flex-col gap-1 ${containerClassName}`}>
      {label && <label className={labelClassName}>{label}</label>}

      <div className="relative h-full w-full">
        <div
          className={`select-none w-full cursor-pointer flex items-center gap-[6px] justify-between ${iconLeft ? "flex-row-reverse" : ""} ${inputClassName}`}
          onClick={handleContainerClick}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          ref={inputRef}
        >
          {Icon && <Icon className={`shrink-0 ${iconClassName}`} />}
          <span
            className={`min-w-0 flex-1 truncate font-normal ${
              value
                ? "text-heading-text dark:text-heading-text-dk"
                : "text-gray-placeholder dark:text-gray-placeholder-dk"
            }`}
            title={value ? new Date(value).toLocaleDateString() : placeholder}
          >
            {value ? new Date(value).toLocaleDateString() : placeholder}
          </span>
        </div>

        {calendarMarkup
          ? createPortal(
              calendarMarkup,
              menuPortalTarget ?? document.fullscreenElement ?? document.body
            )
          : null}
      </div>
    </div>
  );
};

export default SingleDatePicker;
